import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { errorReason } from '../lib/api';
import {
  MAX_CHECKOUT_SEATS,
  SELF_SERVE_TIERS,
  type SelfServeTier,
  startSeatCheckout,
} from '../lib/billing';
import type { AccountKind } from '../lib/session';
import { Select } from './Select';
import { buttonClass } from './ui/Button';

/** The tier control's id. One cart per page, so one id is enough. */
const TIER_ID = 'billing-buy-tier';

// Buy-seats cart (arc 101 slice 07): tier `.select-trigger` + seat-count
// `.input` + primary `.btn`, same row grammar as the spend-cap editor. Submits
// to `POST /v1/billing/checkout` and hands the browser to Stripe's hosted page.
//
// The Portal can only MODIFY a subscription, so this form is the only self-serve
// way an account gets its FIRST one. Presenter: it owns just the cart and the
// in-flight flag, takes the account's `kind` as a prop from its container, and
// sends outcomes through the parent's toast (`onNotice`), never `window.alert` —
// a native modal is off-design and blocks the page.
//
// ## The two carts this form cannot build
//
// `POST /v1/billing/checkout` answers 409 to a Solo cart on an ORG account
// (`solo_requires_personal_account`) and to a Solo cart with `quantity > 1`
// (`solo_is_single_seat`) — Solo is one AI dev on a personal GitHub account at a
// flat price. Those refusals are the BACKSTOP; this form's job is that neither
// is reachable: an org is offered Team only, and Solo renders no seat control.
// A refusal that does arrive is surfaced verbatim through `errorReason`, like
// every other one — the server is the arbiter and its `detail` is the answer.
export function SeatCheckout(props: {
  readonly token: string | null;
  /**
   * The active account's kind, narrowed by `accountKind()` (`lib/session.ts`)
   * from what `/v1/me` sent. Optional and fail-OPEN: `undefined` means NOT
   * KNOWN — an unread `me`, or a kind this client does not recognise — not "an
   * org", so Solo stays on the menu and the 409 arbitrates. Same direction as
   * the `write:billing` scope gate (`useScopeVerdict`): a wrongly shown option
   * costs one toast, a wrongly hidden one costs a personal account the only
   * plan it can buy.
   */
  readonly accountKind?: AccountKind | undefined;
  /** Surface a failure. Optional so the form still renders standalone. */
  readonly onNotice?: (message: string, tone: 'success' | 'error') => void;
}): JSX.Element {
  const [tier, setTier] = createSignal<SelfServeTier>('pro');
  const [seats, setSeats] = createSignal('1');
  const [busy, setBusy] = createSignal(false);

  const isOrg = (): boolean => props.accountKind === 'org';
  const tiers = (): readonly SelfServeTier[] =>
    isOrg() ? SELF_SERVE_TIERS.filter((name) => name !== 'solo') : SELF_SERVE_TIERS;
  /**
   * The tier actually bought. DERIVED rather than synced through an effect, so
   * a `kind` that resolves to `'org'` after the buyer already picked Solo can
   * never leave a stale `solo` in the signal to POST.
   */
  const cartTier = (): SelfServeTier => (isOrg() ? 'pro' : tier());
  const isSolo = (): boolean => cartTier() === 'solo';

  const buy = async (): Promise<void> => {
    // The lock, read on the way in: `disabled` is a rendering, and a second
    // submit landing in the same tick reaches this handler directly. `busy` is
    // set before the first await, so the second one returns here.
    if (busy()) return;
    // Solo is a flat plan: one AI dev, no seat control, so the count is not the
    // buyer's to get wrong.
    const quantity = isSolo() ? 1 : Number(seats());
    // Mirrors the route's Zod cart (positive int ≤ MAX_CHECKOUT_SEATS) so a
    // fat-fingered count is caught before it mints a recurring charge.
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > MAX_CHECKOUT_SEATS) {
      props.onNotice?.(t('billing.buy_seats_quantity_error', { max: MAX_CHECKOUT_SEATS }), 'error');
      return;
    }
    setBusy(true);
    // ONE attempt per click (plan 101 06-7): the API scopes the Stripe session to
    // this key, so a resend of it answers with the same session. A later click,
    // after a failure, is a new attempt and mints a new key.
    const attemptKey = crypto.randomUUID();
    try {
      await startSeatCheckout({ tier: cartTier(), quantity }, props.token, attemptKey);
      // Success navigates to Stripe — stay busy so a second click can't mint a
      // second session while the browser is on its way out.
    } catch (error) {
      props.onNotice?.(errorReason(error, t('billing.buy_seats_error')), 'error');
      setBusy(false);
    }
  };

  return (
    <form
      class="billing-buy"
      onSubmit={(e) => {
        e.preventDefault();
        void buy();
      }}
    >
      <label id={`${TIER_ID}-label`} for={TIER_ID} class="sr-only">
        {t('billing.buy_seats_tier_label')}
      </label>
      <Select
        a11y={{ id: TIER_ID }}
        label={t('billing.buy_seats_tier_label')}
        value={cartTier()}
        disabled={busy()}
        options={tiers().map((name) => ({ value: name, label: t(`billing.tier_${name}`) }))}
        onChange={(value) => setTier(value as SelfServeTier)}
      />
      {/* Team only. Solo has no quantity to choose, and a hidden control is the
          reason `solo_is_single_seat` is unreachable from here. */}
      <Show
        when={!isSolo()}
        fallback={<p class="billing-buy__note">{t('billing.buy_seats_solo_note')}</p>}
      >
        <input
          class="input billing-buy__seats"
          type="number"
          min="1"
          max={MAX_CHECKOUT_SEATS}
          step="1"
          aria-label={t('billing.buy_seats_quantity_label')}
          value={seats()}
          disabled={busy()}
          onInput={(e) => setSeats(e.currentTarget.value)}
        />
      </Show>
      <button type="submit" class={buttonClass()} disabled={busy()}>
        {t('billing.buy_seats_submit')}
      </button>
    </form>
  );
}
